
import { Component, Vue, Watch } from 'vue-property-decorator';
import { isCartBlockedSession } from '@/utils/roles';


import CPSkeletonLoader from '@/components/globals/CPSkeletonLoader.vue';
import { Chart } from 'chart.js';

@Component({
  components: {
    CPSkeletonLoader,
  },
})
export default class CPFundDetails extends Vue {

  public get fundDetails() {
    return this.$store.state.fundDetailsModal.details;
  }

  public get fundInformation() {
    return this.fundDetails.FundFactSheetInformation.dsFundFactSheet[0];
  }

  public get fundHoldings() {
    return this.fundDetails.FundPortHoldingInformation.dsTopFundHoldings;
  }
  public get fundSectors() {
    return this.fundDetails.TopFundSectorsInformation.dsTopFundSectors;
  }
  public get fundRegions() {
    return this.fundDetails.TopFundRegionsInformation.dsTopFundRegions;
  }
  public get fundPerformance() {
    return this.fundDetails?.FundPerformancesInformation?.dsFundPerformance;
  }

  get roles() {
    return this.$store.state.account.details.role;
  }

  public get allYears() {
    const years: number[] = [];
    const allYears: any[] = [];
    if (this.fundPerformance) {
      this.fundPerformance.filter((item: any) => {
        if (years.includes(item.Year) === false) {
          years.push(item.Year);
          allYears.push(item);
        }
      });
    }
    return allYears;
  }

  public get portfolioAnalysis() {
    return [
      {
        name: 'Stock',
        amt: this.fundInformation.AssetAllocationStock,
      },
      {
        name: 'Bond',
        amt: this.fundInformation.AssetAllocationBond,
      },
      {
        name: 'Cash',
        amt: this.fundInformation.AssetAllocationCash,
      },
      {
        name: 'Other',
        amt: this.fundInformation.AssetAllocationOther,
      },
    ];
  }

  public get lineData() {
    const fullInfo: any = {
      fundPerformance: {
        label: 'Fund Performance',
        list: [],
      },
      indexPerformance: {
        label: 'Index Performance',
        list: [],
      },
      categoryPerformance: {
        label: 'Category Performance',
        list: [],
      },
      labels: [],
    };

    if (this.fundPerformance) {
      this.fundPerformance.forEach((item: any) => {
        fullInfo.fundPerformance.list.push(parseFloat((item.FundGrowthValue * 10).toFixed(2)));
        fullInfo.indexPerformance.list.push(parseFloat((item.IndexGrowthValue * 10).toFixed(2)));
        fullInfo.categoryPerformance.list.push(parseFloat(item.CategoryPerformance.toFixed(2)));
        const date: string = item.EndDate.slice(0, 4);
        if (fullInfo.labels.includes(date)) {
          fullInfo.labels.push('');
        } else {
          fullInfo.labels.push(date);
        }
      });
      return fullInfo;
    } else {
      return null;
    }
  }

  public get fetching() { return this.$store.state.watchlist.fetching; }

  public downloading: boolean = false;


  public risk = {
    itemRisk: 1,
    categoryRiskAverage: 5,
  };

  public boxInfo = {
    boxCount: 9,
    active: 4,
  };

    // get current date
  public date: Date = new Date();
  public dd = String(this.date.getDate()).padStart(2, '0');
  public mm = String(this.date.getMonth() + 1).padStart(2, '0'); // January is 0!
  public yyyy = this.date.getFullYear();
  public formattedDate = this.dd + '/' + this.mm + '/' + this.yyyy;

  public donutData: any[] = [];

  public colours = [
    '#1F2041',
    '#D0D6B5',
    '#FFC857',
    '#987284',
    '#119DA4',
    '#4B3F72',
    '#19647E',
    '#75B9BE',
    '#EAF9D9',
    '#F9B5AC',
    '#EE7674',
    '#CCC7B9',
    '#AF7A6D',
    '#653239',
    '#62929E',
  ];


  // Line Chart via Chart.js

  // Chart Data
  public chartEl: any = null;

  public myChart: any;
  public chart = this.$refs.chart;

  public title: string = 'Yearly Performance';

  public async downloadReport() {
    this.downloading = true;
    await this.$store.dispatch('getFundDetailsURL', this.fundInformation.ISIN);
    this.downloading = false;
  }


  public getDonutData() {
    // base offset is 25% to represent a donut starting at 12 o'clock rather than 3 o'clock with 0 offset.
    let assetTotal = 25;
    const holdingsTotal = 25;
    this.portfolioAnalysis.forEach((item: any) => {
      item.dashArray = (item.amt) + ' ' + (100 - item.amt);
      item.offset = (assetTotal + item.amt);
      this.donutData.push(item);
      assetTotal = assetTotal + item.amt;
    });
  }

  public getPositionOfRisk(num: number) {
    switch (num) {
      case 1:
        return 20;
      case 2:
        return 33.33;
      case 3:
        return 50;
      case 4:
        return 66.66;
      case 5:
        return 80;
      default:
        break;
    }
  }

  @Watch('lineData')
  public rerunChart() {
    this.createLineChart();
  }

  public createLineChart() {
    this.myChart = new Chart(this.chartEl, {
      type: 'line',
      data: {
        labels: this.lineData.labels,
        datasets: [
          {
            label: this.lineData.fundPerformance.label,
            data: this.lineData.fundPerformance.list,
            pointRadius: 0.1,
            pointHitRadius: 5,
            pointHoverRadius: 5,
            borderColor: this.colours[0],
            borderWidth: 2,
            lineTension: 0.1,
            backgroundColor: 'transparent',
          },
          {
            label: this.lineData.indexPerformance.label,
            data: this.lineData.indexPerformance.list,
            pointRadius: 0.1,
            pointHitRadius: 5,
            pointHoverRadius: 5,
            borderWidth: 2,
            borderColor: this.colours[2],
            backgroundColor: 'transparent',
          },
          {
            label: this.lineData.categoryPerformance.label,
            data: this.lineData.categoryPerformance.list,
            pointRadius: 0.1,
            pointHitRadius: 5,
            pointHoverRadius: 5,
            borderColor: this.colours[4],
            borderWidth: 2,
            backgroundColor: 'transparent',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 1.8,
        animation: {
          duration: 2500,
          easing: 'easeInOutCubic',
        },
        legend: {
          display: false,
        },
        scales: {
          yAxes: [{
            ticks: {
              fontSize: 9,
            },
          }],
          xAxes: [{
            ticks: {
              fontSize: 9,
            },
          }],
        },
      },
    });
    this.myChart.update();
  }

  public truncateText(text: string, truncateLength: number) {
    return text.length > truncateLength ? text.substring(0, truncateLength) + '...' : text;
  }

  @Watch('fundDetails')
  public createCharts() {
    setTimeout(() => {
      this.chartEl = this.$refs.chart;
      this.getDonutData();
      this.createLineChart();
    }, 300);
  }


  public buyItem(result: any) {
    // TODO - missing data required to open basket e.g. ProductType
    if (isCartBlockedSession(this.$store.state.account.details)) {
      this.$store.dispatch('addError', 'Buy feature is not available, Please login as the client to continue with buy/sell process');
    } else {
      this.$store.dispatch('updateBuyBasketDetails', result);
      this.$store.commit('toggleBuyBasket', true);

    }
  }

  public addToWatchlist(item: Product) {
    this.$store.dispatch('addToWatchlist', item);
  }

  public setActiveWatchlist(item: Product) {
    this.$store.dispatch('setActiveWatchlistItem', item);
  }

  public beforeDestroy() {
    this.$store.commit('buyFundDetails', null);
  }
}
